import { format } from 'date-fns';
import { CalendarIcon, XIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';

export function DateRangePicker({
  value,
  onChange,
  className,
  placeholder = 'Pick a date range',
  clearable = false,
}: {
  value?: DateRange;
  onChange: (dateRange: DateRange | undefined) => void;
  className?: string;
  placeholder?: string;
  clearable?: boolean;
}) {
  const gt = useGT();
  function formatShortDate(date: Date) {
    return format(date, 'MMM d, yyyy');
  }

  function getRangeLabel() {
    if (!value?.from && !value?.to) {
      return placeholder;
    }

    if (value?.from && !value?.to) {
      return formatShortDate(value.from);
    }

    if (!value?.from && value?.to) {
      return formatShortDate(value.to);
    }

    if (value?.from && value?.to) {
      const fromDate = value.from;
      const toDate = value.to;
      const sameYear = fromDate.getFullYear() === toDate.getFullYear();
      const sameMonth = sameYear && fromDate.getMonth() === toDate.getMonth();

      if (sameMonth) {
        return `${format(fromDate, 'MMM d')} - ${format(toDate, 'd, yyyy')}`;
      }

      if (sameYear) {
        return `${format(fromDate, 'MMM d')} - ${format(toDate, 'MMM d, yyyy')}`;
      }

      return `${formatShortDate(fromDate)} - ${formatShortDate(toDate)}`;
    }

    return placeholder;
  }

  const rangeLabel = getRangeLabel();
  const hasSelection = Boolean(value?.from || value?.to);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-empty={!hasSelection}
          className={cn(
            'data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal h-8',
            className,
          )}
        >
          <CalendarIcon />
          <span className="flex-1 text-left">{rangeLabel}</span>
          {clearable && hasSelection && (
            <span
              role="button"
              tabIndex={0}
              aria-label={gt('Clear date range', {
                context:
                  'aria label for button that clears the selected date range',
              })}
              className="p-0.5 hover:bg-accent rounded-sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChange(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(undefined);
                }
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
            >
              <XIcon className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="range"
          selected={value}
          defaultMonth={value?.from || new Date()}
          onSelect={onChange}
          required
        />
      </PopoverContent>
    </Popover>
  );
}
